import * as i3 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Pipe, EventEmitter, Component, ChangeDetectionStrategy, Inject, Output, Input, HostListener, NgModule } from '@angular/core';
import { TUI_EDITOR_LINK_HASH_PREFIX, TUI_EDITOR_LINK_OSI_PROTOCOL_DIVIDER, TUI_EDITOR_LINK_SIMPLE_PROTOCOL_DIVIDER, TUI_EDITOR_LINK_HTTPS_PREFIX } from '@taiga-ui/addon-editor/constants';
import { TuiTiptapEditorService } from '@taiga-ui/addon-editor/directives';
import { TUI_EDITOR_LINK_TEXTS, TUI_EDITOR_OPTIONS } from '@taiga-ui/addon-editor/tokens';
import * as i4 from '@taiga-ui/cdk';
import { tuiIsValidUrl, tuiIsElement, TuiAutoFocusModule } from '@taiga-ui/cdk';
import * as i1 from '@taiga-ui/kit';
import { TuiInputInlineModule, TuiToggleModule } from '@taiga-ui/kit';
import * as i2 from '@taiga-ui/core';
import { TuiButtonModule, TuiSvgModule, TuiLinkModule, TuiScrollbarModule } from '@taiga-ui/core';
import * as i5 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import * as i8 from '@taiga-ui/addon-editor/abstract';

function tuiEditLinkParseUrl(url = ``) {
    if (url.startsWith(TUI_EDITOR_LINK_HASH_PREFIX)) {
        return { prefix: TUI_EDITOR_LINK_HASH_PREFIX, path: url.slice(1) };
    }
    const [prefix = ``, path = ``] = url.includes(TUI_EDITOR_LINK_OSI_PROTOCOL_DIVIDER)
        ? splitOsiProtocol(url)
        : splitSimpleProtocol(url);
    if (path.includes(TUI_EDITOR_LINK_SIMPLE_PROTOCOL_DIVIDER)) {
        const [protocol, otherPart] = splitSimpleProtocol(path);
        if (protocol && otherPart) {
            return { prefix: protocol, path: otherPart };
        }
    }
    return { prefix, path: prefix === `` ? url : path };
}
function splitOsiProtocol(url = ``) {
    var _a;
    const protocolPosition = (_a = url.indexOf(TUI_EDITOR_LINK_OSI_PROTOCOL_DIVIDER)) !== null && _a !== void 0 ? _a : -1;
    const [prefix, path] = protocolPosition > -1
        ? [
            url.slice(0, protocolPosition + TUI_EDITOR_LINK_OSI_PROTOCOL_DIVIDER.length),
            url.slice(protocolPosition + TUI_EDITOR_LINK_OSI_PROTOCOL_DIVIDER.length, url.length),
        ]
        : [``, url];
    const result = [prefix, path].filter(Boolean);
    return (path === null || path === void 0 ? void 0 : path.includes(`://`)) && result.length > 1 ? splitOsiProtocol(path) : result;
}
function splitSimpleProtocol(url = ``) {
    const [prefix, path] = url.split(/:/).slice(-2).filter(Boolean);
    return prefix && path && !tuiIsValidUrl(url) ? [`${prefix}:`, path] : [];
}

const MAX_LENGTH = 60;
const START = MAX_LENGTH - 20;
const END = MAX_LENGTH - START - 10;
class TuiShortUrlPipe {
    transform(url) {
        return url.length < MAX_LENGTH
            ? url
            : `${url.slice(0, Math.max(0, START))}...${url.slice(url.length - END)}`;
    }
}
TuiShortUrlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiShortUrlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
TuiShortUrlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiShortUrlPipe, name: "tuiShortUrl" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiShortUrlPipe, decorators: [{
            type: Pipe,
            args: [{ name: `tuiShortUrl` }]
        }] });

class TuiFilterAnchorsPipe {
    transform(anchors, prefix, currentUrl) {
        return prefix === `#` ? anchors.filter(anchor => anchor !== currentUrl) : anchors;
    }
}
TuiFilterAnchorsPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFilterAnchorsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
TuiFilterAnchorsPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFilterAnchorsPipe, name: "tuiFilterAnchors" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiFilterAnchorsPipe, decorators: [{
            type: Pipe,
            args: [{ name: `tuiFilterAnchors` }]
        }] });

class TuiEditLinkComponent {
    constructor(doc, texts$, editor, options) {
        this.doc = doc;
        this.texts$ = texts$;
        this.editor = editor;
        this.options = options;
        this.isOnlyAnchorMode = this.detectAnchorMode();
        this.addLink = new EventEmitter();
        this.removeLink = new EventEmitter();
        this.url = this.getHrefOrAnchorId();
        this.edit = !this.url;
        this.prefix = this.makeDefaultPrefix();
        this.anchorIds = this.getAllAnchorsIds();
    }
    get defaultProtocol() {
        var _a, _b;
        return (_b = (_a = this.options.linkOptions) === null || _a === void 0 ? void 0 : _a.protocol) !== null && _b !== void 0 ? _b : TUI_EDITOR_LINK_HTTPS_PREFIX;
    }
    set anchorMode(mode) {
        this.isOnlyAnchorMode = mode;
        this.prefix = mode ? TUI_EDITOR_LINK_HASH_PREFIX : this.makeDefaultPrefix();
    }
    get anchorMode() {
        return this.isOnlyAnchorMode;
    }
    get prefixIsHashMode() {
        return this.prefix === TUI_EDITOR_LINK_HASH_PREFIX;
    }
    get hasUrl() {
        return !!this.url;
    }
    get href() {
        return `${this.prefix}${this.url}`;
    }
    get showAnchorsList() {
        return !this.anchorMode && this.edit && this.anchorIds.length > 0;
    }
    get isViewMode() {
        return !this.edit;
    }
    onSelectionChange() {
        if (this.isViewMode) {
            this.url = this.getHrefOrAnchorId();
            this.anchorMode = this.detectAnchorMode();
        }
    }
    onMouseDown(event) {
        if (tuiIsElement(event.target) && !event.target.matches('a, button, input')) {
            event.preventDefault();
        }
    }
    setAnchor(anchor) {
        this.url = anchor;
        this.changePrefix(true);
    }
    changePrefix(isPrefix) {
        this.prefix = isPrefix ? TUI_EDITOR_LINK_HASH_PREFIX : this.defaultProtocol;
    }
    onSave() {
        if (this.url) {
            this.addLink.emit(this.href);
        }
        else {
            this.removeLink.emit();
        }
    }
    onBackspace() {
        if (!this.url) {
            this.prefix = this.isOnlyAnchorMode
                ? TUI_EDITOR_LINK_HASH_PREFIX
                : this.defaultProtocol;
        }
    }
    onEdit() {
        this.edit = true;
    }
    onRemove() {
        this.removeLink.emit();
    }
    onChange(url) {
        this.url = this.removePrefix(url);
    }
    onClear() {
        this.url = '';
    }
    makeDefaultPrefix() {
        var _a, _b;
        const a = this.getAnchorElement();
        const defaultPrefix = tuiEditLinkParseUrl((_a = a === null || a === void 0 ? void 0 : a.getAttribute('href')) !== null && _a !== void 0 ? _a : '')
            .prefix || this.defaultProtocol;
        if (a) {
            return (!a.getAttribute('href') && a.getAttribute('id')) ||
                ((_b = a.getAttribute('href')) === null || _b === void 0 ? void 0 : _b.startsWith(TUI_EDITOR_LINK_HASH_PREFIX))
                ? TUI_EDITOR_LINK_HASH_PREFIX
                : defaultPrefix;
        }
        return defaultPrefix;
    }
    detectAnchorMode() {
        const a = this.getAnchorElement();
        return !(a === null || a === void 0 ? void 0 : a.href) && !!(a === null || a === void 0 ? void 0 : a.getAttribute('id'));
    }
    getFocusedParentElement() {
        var _a, _b;
        return ((_b = (_a = this.doc.getSelection()) === null || _a === void 0 ? void 0 : _a.focusNode) === null || _b === void 0 ? void 0 : _b.parentElement) || null;
    }
    getAnchorElement() {
        var _a, _b, _c;
        const focusable = this.getFocusedParentElement();
        return ((_c = (_a = focusable === null || focusable === void 0 ? void 0 : focusable.closest('a')) !== null && _a !== void 0 ? _a : (_b = focusable === null || focusable === void 0 ? void 0 : focusable.querySelector('img')) === null || _b === void 0 ? void 0 : _b.closest('a')) !== null && _c !== void 0 ? _c : null);
    }
    getHrefOrAnchorId() {
        const a = this.getAnchorElement();
        return a
            ? this.removePrefix(a.getAttribute('href') || a.getAttribute('id') || '')
            : this.url;
    }
    removePrefix(url) {
        var _a;
        const fullPath = url.startsWith(TUI_EDITOR_LINK_HASH_PREFIX) ||
            this.prefix === TUI_EDITOR_LINK_HASH_PREFIX
            ? url
            : `${(_a = this.prefix) !== null && _a !== void 0 ? _a : ''}${url}`;
        const { prefix, path } = tuiEditLinkParseUrl(fullPath);
        const expectAnchorMode = this.isOnlyAnchorMode ||
            prefix === TUI_EDITOR_LINK_HASH_PREFIX ||
            (prefix === '' && this.prefix === TUI_EDITOR_LINK_HASH_PREFIX);
        if (expectAnchorMode) {
            this.prefix = TUI_EDITOR_LINK_HASH_PREFIX;
        }
        else if (prefix === '') {
            this.prefix = this.defaultProtocol;
        }
        else {
            this.prefix = prefix;
        }
        return path;
    }
    getAllAnchorsIds() {
        var _a;
        const nodes = Array.from((_a = this.editor
            .getOriginTiptapEditor()
            .view.dom.querySelectorAll('[data-type="jump-anchor"]')) !== null && _a !== void 0 ? _a : []);
        return Array.from(nodes)
            .map(node => node.getAttribute('id') || '')
            .filter(Boolean);
    }
}
TuiEditLinkComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditLinkComponent, deps: [{ token: DOCUMENT }, { token: TUI_EDITOR_LINK_TEXTS }, { token: TuiTiptapEditorService }, { token: TUI_EDITOR_OPTIONS }], target: i0.ɵɵFactoryTarget.Component });
TuiEditLinkComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiEditLinkComponent, selector: "tui-edit-link", inputs: { anchorMode: "anchorMode" }, outputs: { addLink: "addLink", removeLink: "removeLink" }, host: { listeners: { "document:selectionchange": "onSelectionChange()", "mousedown": "onMouseDown($event)" } }, ngImport: i0, template: "<div class=\"t-edit-link\">\n    <ng-container *ngIf=\"edit; else view\">\n        <label class=\"t-label\">\n            <div class=\"t-label-properties\">\n                <ng-container *ngIf=\"anchorMode; else fullOptions\">Edit anchor</ng-container>\n                <ng-template #fullOptions>\n                    <span *ngIf=\"prefixIsHashMode; else urlTitle\">Link to anchor</span>\n                    <ng-template #urlTitle>\n                        <span>URL</span>\n                    </ng-template>\n                </ng-template>\n            </div>\n            <div class=\"t-url\">\n                <div class=\"t-prefix\">{{ prefix }}</div>\n                <tui-input-inline\n                    tuiAutoFocus\n                    class=\"t-input\"\n                    [class.t-input_filled]=\"hasUrl\"\n                    [(ngModel)]=\"url\"\n                    (input)=\"onChange($any($event.target)?.value)\"\n                    (keydown.prevent.enter)=\"onSave()\"\n                    (keydown.backspace)=\"onBackspace()\"\n                >\n                    <ng-container *ngIf=\"texts$ | async as text\">\n                        {{ prefixIsHashMode ? text.anchorExample : text.urlExample }}\n                    </ng-container>\n                </tui-input-inline>\n            </div>\n        </label>\n        <tui-svg\n            src=\"tuiIconCloseLarge\"\n            class=\"t-cleaner\"\n            [class.t-cleaner_hidden]=\"!hasUrl\"\n            (click)=\"onClear()\"\n        ></tui-svg>\n        <button\n            tuiIconButton\n            type=\"button\"\n            size=\"s\"\n            title=\"Insert link\"\n            icon=\"tuiIconCheckCircleLarge\"\n            shape=\"rounded\"\n            appearance=\"icon\"\n            class=\"t-button\"\n            (click)=\"onSave()\"\n        ></button>\n    </ng-container>\n    <ng-template #view>\n        <a\n            tuiLink\n            target=\"_blank\"\n            iconAlign=\"left\"\n            class=\"t-link\"\n            [href]=\"href\"\n            [icon]=\"prefixIsHashMode ? 'tuiIconHash' : 'tuiIconExternalLink'\"\n        >\n            {{ url | tuiShortUrl }}\n        </a>\n        <button\n            tuiIconButton\n            type=\"button\"\n            size=\"s\"\n            title=\"Change link\"\n            icon=\"tuiIconEdit2Large\"\n            shape=\"rounded\"\n            appearance=\"icon\"\n            class=\"t-button t-edit-button\"\n            (click)=\"onEdit()\"\n        ></button>\n        <button\n            tuiIconButton\n            type=\"button\"\n            size=\"s\"\n            title=\"Remove link\"\n            icon=\"tuiIconUnlinkLarge\"\n            shape=\"rounded\"\n            appearance=\"icon\"\n            class=\"t-button\"\n            (click)=\"onRemove()\"\n        ></button>\n    </ng-template>\n</div>\n\n<ng-container *ngIf=\"showAnchorsList\">\n    <hr class=\"t-hr\" />\n    <tui-scrollbar>\n        <div class=\"t-anchor-list\">\n            <button\n                *ngFor=\"let id of anchorIds | tuiFilterAnchors : prefix : url\"\n                type=\"button\"\n                class=\"t-anchor\"\n                (click)=\"setAnchor(id)\"\n            >\n                #{{ id }}\n            </button>\n        </div>\n    </tui-scrollbar>\n</ng-container>\n", styles: [":host{display:block;min-width:18.75rem}.t-edit-link{display:flex;min-height:4rem;align-items:center;padding-right:.75rem}.t-label{font:var(--tui-font-text-s);height:var(--tui-height-l);box-sizing:border-box;padding:.4375rem 1rem;min-width:12.5rem;max-width:25rem;width:100%}.t-label-properties{display:flex;align-items:center;max-width:8.375rem;justify-content:space-between}.t-property_inactive{opacity:.5}.t-url{display:flex;font:var(--tui-font-text-m);color:var(--tui-text-02)}.t-input{flex:1}.t-input_filled{color:var(--tui-text-01)}.t-button{width:2rem;height:2rem;margin:0 .25rem;transition:background .2s}.t-edit-button{margin-left:auto}.t-link{margin:0 1rem}.t-cleaner{transition-property:color;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;color:var(--tui-base-06);cursor:pointer;margin-left:auto}.t-cleaner:hover{color:var(--tui-base-07)}.t-cleaner_hidden{visibility:hidden}.t-anchor-list{display:flex;max-height:12.5rem;flex-direction:column}.t-anchor{-webkit-appearance:none;-moz-appearance:none;appearance:none;border:0;background:none;font-size:inherit;line-height:inherit;min-height:2.75rem;padding:.375rem .5rem;margin:.125rem .5rem;border-radius:var(--tui-radius-m);text-align:left}.t-anchor:hover{background:var(--tui-base-02);cursor:pointer}\n"], components: [{ type: i1.TuiInputInlineComponent, selector: "tui-input-inline", inputs: ["maxLength"] }, { type: i2.TuiSvgComponent, selector: "tui-svg", inputs: ["src"] }, { type: i2.TuiButtonComponent, selector: "button[tuiButton], button[tuiIconButton], a[tuiButton], a[tuiIconButton]", inputs: ["appearance", "disabled", "icon", "iconRight", "shape", "showLoader", "size"] }, { type: i2.TuiLinkComponent, selector: "a[tuiLink], button[tuiLink]", inputs: ["pseudo", "icon", "iconAlign", "iconRotated", "mode"], exportAs: ["tuiLink"] }, { type: i2.TuiScrollbarComponent, selector: "tui-scrollbar", inputs: ["hidden"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.TuiAutoFocusDirective, selector: "[tuiAutoFocus]", inputs: ["tuiAutoFocus"] }, { type: i5.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i5.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], pipes: { "async": i3.AsyncPipe, "tuiShortUrl": TuiShortUrlPipe, "tuiFilterAnchors": TuiFilterAnchorsPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditLinkComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-edit-link',
                    templateUrl: './edit-link.template.html',
                    styleUrls: ['./edit-link.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_LINK_TEXTS]
                }] }, { type: i8.AbstractTuiEditor, decorators: [{
                    type: Inject,
                    args: [TuiTiptapEditorService]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_OPTIONS]
                }] }]; }, propDecorators: { addLink: [{
                type: Output
            }], removeLink: [{
                type: Output
            }], anchorMode: [{
                type: Input
            }], onSelectionChange: [{
                type: HostListener,
                args: ['document:selectionchange']
            }], onMouseDown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }] } });

class TuiEditLinkModule {
}
TuiEditLinkModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditLinkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiEditLinkModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditLinkModule, declarations: [TuiEditLinkComponent, TuiShortUrlPipe, TuiFilterAnchorsPipe], imports: [CommonModule,
        FormsModule,
        TuiAutoFocusModule,
        TuiButtonModule,
        TuiSvgModule,
        TuiLinkModule,
        TuiInputInlineModule,
        TuiToggleModule,
        TuiScrollbarModule], exports: [TuiEditLinkComponent] });
TuiEditLinkModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditLinkModule, imports: [[
            CommonModule,
            FormsModule,
            TuiAutoFocusModule,
            TuiButtonModule,
            TuiSvgModule,
            TuiLinkModule,
            TuiInputInlineModule,
            TuiToggleModule,
            TuiScrollbarModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditLinkModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        TuiAutoFocusModule,
                        TuiButtonModule,
                        TuiSvgModule,
                        TuiLinkModule,
                        TuiInputInlineModule,
                        TuiToggleModule,
                        TuiScrollbarModule,
                    ],
                    declarations: [TuiEditLinkComponent, TuiShortUrlPipe, TuiFilterAnchorsPipe],
                    exports: [TuiEditLinkComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TuiEditLinkComponent, TuiEditLinkModule, TuiFilterAnchorsPipe, TuiShortUrlPipe, tuiEditLinkParseUrl };
